import { useEffect, useState } from 'react';

export type SystemTransitionPhase = 'shown' | 'transitioning' | 'showing';

// The fade (HIDE_MS/SHOW_MS) and the background ramp (BACKGROUND_RAMP_MS)
// now start together off the same phase, so the swap only needs to wait out
// whichever of the two is longer.
const HIDE_MS = 200;
const SHOW_MS = 200;
const BACKGROUND_RAMP_MS = 600;
const TRANSITION_OUT_MS = Math.max(HIDE_MS, BACKGROUND_RAMP_MS);

/**
 * Drives the hide → swap → show hand-off for switching between two
 * async-loaded views keyed by `targetKey` (e.g. a "galaxy:system" pair),
 * keeping the previously-displayed view on screen until the new one is
 * actually ready.
 *
 *   'shown'         → steady state, `displayedView` matches `targetKey`.
 *   'transitioning' → starts the instant `targetKey` changes; fade-out and
 *                      background ramp-up both key off this phase, so they
 *                      start together. Waits for BOTH TRANSITION_OUT_MS to
 *                      elapse (long enough for the fade and the Starfield's
 *                      own speed easing to actually finish) AND the new
 *                      view to be ready — react-query's `isPending` alone
 *                      isn't reliable here, since a cache hit within
 *                      `staleTime` skips pending entirely.
 *   'showing'       → entered once both conditions above are met, at the
 *                      same moment `displayedView` swaps to the new view;
 *                      fade-in and background ramp-down both key off this
 *                      phase too. Settles back to 'shown' after SHOW_MS.
 */
export function useSystemTransition<T>(
    targetKey: string | null,
    view: T | undefined,
    viewKey: (view: T) => string,
): { phase: SystemTransitionPhase; displayedView: T | undefined } {
    const [phase, setPhase] = useState<SystemTransitionPhase>('shown');
    const [displayedView, setDisplayedView] = useState<T | undefined>(undefined);
    const displayedKey = displayedView ? viewKey(displayedView) : null;
    const dataReadyForTarget = targetKey !== null && view !== undefined && viewKey(view) === targetKey;

    // Kick the sequence off whenever navigation targets a key that isn't
    // what's currently on screen.
    useEffect(() => {
        if (targetKey === null || targetKey === displayedKey) return;
        setPhase('transitioning');
        // Only the *event* of the target changing should (re)start the
        // sequence — displayedKey is read for its value at that moment, not
        // tracked as a re-trigger source (it changes as a RESULT of the
        // sequence this effect drives, which would otherwise cause a loop).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetKey]);

    const [transitionOutElapsed, setTransitionOutElapsed] = useState(false);
    useEffect(() => {
        if (phase !== 'transitioning') {
            setTransitionOutElapsed(false);
            return;
        }
        const timer = setTimeout(() => setTransitionOutElapsed(true), TRANSITION_OUT_MS);
        return () => clearTimeout(timer);
    }, [phase]);

    useEffect(() => {
        if (phase === 'transitioning' && transitionOutElapsed && dataReadyForTarget) {
            setDisplayedView(view);
            setPhase('showing');
        }
    }, [phase, transitionOutElapsed, dataReadyForTarget, view]);

    useEffect(() => {
        if (phase !== 'showing') return;
        const timer = setTimeout(() => setPhase('shown'), SHOW_MS);
        return () => clearTimeout(timer);
    }, [phase]);

    return { phase, displayedView };
}
